import { useState } from "react";
import axios from "axios";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { History, Plus, Pencil, Trash2, ArrowRight } from "lucide-react";

import { API_URL as API } from "@/lib/api";

const ACTION_ICONS = { crear: Plus, editar: Pencil, eliminar: Trash2 };
const ACTION_COLORS = {
  crear: "text-emerald-600",
  editar: "text-amber-600",
  eliminar: "text-rose-600",
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("es-BO", { day: "2-digit", month: "short" }) +
    " " + d.toLocaleTimeString("es-BO", { hour: "2-digit", minute: "2-digit" });
};

const ProductHistoryPopover = ({ codigo }) => {
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchHistory = async () => {
    if (logs !== null) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/audit-log/product/${encodeURIComponent(codigo)}`, { withCredentials: true });
      setLogs(data.logs || []);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover onOpenChange={(open) => { if (open) fetchHistory(); }}>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-0.5 text-muted-foreground hover:text-[#FF9900] transition-colors"
          data-testid={`product-history-${codigo}`}
          title="Ver historial"
        >
          <History className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="end">
        <div className="flex items-center gap-2 mb-2 pb-2 border-b">
          <History className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-semibold text-foreground">Historial: {codigo}</span>
        </div>
        {loading ? (
          <p className="text-xs text-muted-foreground py-3 text-center">Cargando...</p>
        ) : !logs || logs.length === 0 ? (
          <p className="text-xs text-muted-foreground py-3 text-center">Sin cambios registrados</p>
        ) : (
          <div className="space-y-2 max-h-[250px] overflow-y-auto">
            {logs.slice(0, 10).map((log) => {
              const Icon = ACTION_ICONS[log.action] || Pencil;
              const color = ACTION_COLORS[log.action] || "text-muted-foreground";
              return (
                <div key={log._id} className="flex gap-2 text-xs">
                  <div className="shrink-0 mt-0.5">
                    <Icon className={`w-3.5 h-3.5 ${color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-medium text-foreground capitalize">{log.action}</span>
                      <span className="text-muted-foreground whitespace-nowrap">{formatDate(log.timestamp)}</span>
                    </div>
                    <span className="text-muted-foreground">{log.user_name || log.user_email}</span>
                    {log.action === "editar" && log.details?.cambios && (
                      <div className="mt-1 space-y-0.5">
                        {Object.entries(log.details.cambios).map(([field, val]) => (
                          <div key={field} className="flex items-center gap-1">
                            <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">{field}</Badge>
                            <span className="text-rose-500 line-through truncate max-w-[60px]">{String(val.antes ?? "")}</span>
                            <ArrowRight className="w-2.5 h-2.5 text-slate-300 shrink-0" />
                            <span className="text-emerald-600 truncate max-w-[60px]">{String(val.despues ?? "")}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ProductHistoryPopover;
